
import React, { useState, useEffect } from 'react';
import { X, Users, BarChart3, Calendar, TrendingUp, Clock, Activity, RefreshCw, Shield, Megaphone, Save, Eye, Download } from 'lucide-react';
import { getAdminAnalytics, getModalConfig, updateModalConfig, getAllUsersData } from '../services/adminService';
import { AdminAnalytics, UserStats, AdvertisingModalConfig } from '../types';
import AdvertisingModal from './AdvertisingModal';
import * as XLSX from 'xlsx';

interface AdminDashboardProps {
  isOpen: boolean;
  onClose: () => void;
}

const AdminDashboard: React.FC<AdminDashboardProps> = ({ isOpen, onClose }) => {
  const [analytics, setAnalytics] = useState<AdminAnalytics | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'analytics' | 'modal'>('analytics');

  // Modal config state
  const [modalConfig, setModalConfig] = useState<AdvertisingModalConfig | null>(null);
  const [isSavingModal, setIsSavingModal] = useState(false);
  const [modalSaveSuccess, setModalSaveSuccess] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [isDownloadingExcel, setIsDownloadingExcel] = useState(false);

  const fetchAnalytics = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await getAdminAnalytics();
      setAnalytics(data);
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError('Error al cargar los datos de análisis');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchModalConfig = async () => {
    try {
      const config = await getModalConfig();
      setModalConfig(config);
    } catch (err) {
      console.error('Error fetching modal config:', err);
    }
  };

  const handleSaveModalConfig = async () => {
    if (!modalConfig) return;

    setIsSavingModal(true);
    setModalSaveSuccess(false);
    try {
      await updateModalConfig(modalConfig);
      setModalSaveSuccess(true);
      setTimeout(() => setModalSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Error saving modal config:', err);
      alert('Error al guardar la configuración');
    } finally {
      setIsSavingModal(false);
    }
  };

  const handleDownloadExcel = async () => {
    setIsDownloadingExcel(true);
    try {
      // Fetch all users data
      const usersData = await getAllUsersData();

      // Convert to worksheet
      const worksheet = XLSX.utils.json_to_sheet(usersData);

      // Create workbook
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Usuarios');

      // Generate filename with current date
      const now = new Date();
      const dateStr = now.toISOString().split('T')[0]; // YYYY-MM-DD
      const filename = `usuarios_${dateStr}.xlsx`;

      // Download file
      XLSX.writeFile(workbook, filename);
    } catch (err) {
      console.error('Error downloading Excel:', err);
      alert('Error al descargar el archivo Excel');
    } finally {
      setIsDownloadingExcel(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchAnalytics();
      fetchModalConfig();
    }
  }, [isOpen]);

  const formatDate = (timestamp: number) => {
    return new Intl.DateTimeFormat('es-AR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(new Date(timestamp));
  };

  const formatShortDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return new Intl.DateTimeFormat('es-AR', {
      day: '2-digit',
      month: 'short'
    }).format(date);
  };

  // Calculate max for chart scaling
  const maxDailyCount = analytics?.dailyStats
    ? Math.max(...analytics.dailyStats.map(d => d.analysisCount), 1)
    : 1;

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/80 backdrop-blur-sm z-40 transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
        onClick={onClose}
      />

      {/* Modal Panel */}
      <div
        className={`fixed inset-4 md:inset-10 bg-zinc-900/95 border border-zinc-800 z-50 rounded-2xl transform transition-all duration-300 ease-out shadow-2xl flex flex-col overflow-hidden ${isOpen ? 'scale-100 opacity-100' : 'scale-95 opacity-0 pointer-events-none'
          }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-zinc-800 bg-black/40">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-500/10 rounded-xl border border-purple-500/20">
              <Shield className="w-6 h-6 text-purple-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Admin Dashboard</h2>
              <p className="text-xs text-zinc-500">Estadísticas y análisis de uso</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleDownloadExcel}
              disabled={isDownloadingExcel}
              className="px-3 py-2 text-emerald-400 hover:text-emerald-300 hover:bg-emerald-950/30 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2 border border-emerald-500/20 hover:border-emerald-500/40"
              title="Descargar todos los usuarios en Excel"
            >
              {isDownloadingExcel ? (
                <div className="w-5 h-5 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
              ) : (
                <Download className="w-5 h-5" />
              )}
              <span className="text-sm font-medium hidden md:inline">Exportar Excel</span>
            </button>
            <button
              onClick={fetchAnalytics}
              disabled={isLoading}
              className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-lg transition-colors disabled:opacity-50"
              title="Refrescar datos"
            >
              <RefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={onClose}
              className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-zinc-800 bg-black/20">
          <div className="flex gap-2 px-6">
            <button
              onClick={() => setActiveTab('analytics')}
              className={`px-4 py-3 font-medium text-sm transition-all relative ${activeTab === 'analytics'
                ? 'text-purple-400'
                : 'text-zinc-500 hover:text-zinc-300'
                }`}
            >
              <div className="flex items-center gap-2">
                <BarChart3 className="w-4 h-4" />
                Análisis
              </div>
              {activeTab === 'analytics' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-purple-500" />
              )}
            </button>
            <button
              onClick={() => setActiveTab('modal')}
              className={`px-4 py-3 font-medium text-sm transition-all relative ${activeTab === 'modal'
                ? 'text-cyan-400'
                : 'text-zinc-500 hover:text-zinc-300'
                }`}
            >
              <div className="flex items-center gap-2">
                <Megaphone className="w-4 h-4" />
                Modal Publicitario
              </div>
              {activeTab === 'modal' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-cyan-500" />
              )}
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'analytics' ? (
            <>
              {isLoading && !analytics ? (
                <div className="flex items-center justify-center h-64">
                  <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : error ? (
                <div className="flex flex-col items-center justify-center h-64 text-red-400">
                  <p>{error}</p>
                  <button
                    onClick={fetchAnalytics}
                    className="mt-4 px-4 py-2 bg-red-950/30 border border-red-900/50 rounded-lg hover:bg-red-900/30 transition-colors"
                  >
                    Reintentar
                  </button>
                </div>
              ) : analytics ? (
                <div className="space-y-8">
                  {/* Stats Cards */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <StatCard
                      icon={<Users className="w-5 h-5" />}
                      label="Usuarios Totales"
                      value={analytics.totalUsers}
                      color="cyan"
                    />
                    <StatCard
                      icon={<BarChart3 className="w-5 h-5" />}
                      label="Análisis Totales"
                      value={analytics.totalAnalyses}
                      color="purple"
                    />
                    <StatCard
                      icon={<Calendar className="w-5 h-5" />}
                      label="Análisis Hoy"
                      value={analytics.analysesToday}
                      color="emerald"
                    />
                    <StatCard
                      icon={<TrendingUp className="w-5 h-5" />}
                      label="Esta Semana"
                      value={analytics.analysesThisWeek}
                      color="amber"
                    />
                  </div>

                  {/* Chart - Last 30 Days */}
                  <div className="bg-black/40 border border-zinc-800 rounded-xl p-6">
                    <div className="flex items-center gap-2 mb-6">
                      <Activity className="w-5 h-5 text-cyan-500" />
                      <h3 className="text-lg font-bold text-white">Análisis - Últimos 30 días</h3>
                      <span className="text-sm text-zinc-500 ml-auto">{analytics.analysesThisMonth} total</span>
                    </div>
                    <div className="h-48 flex items-end gap-1">
                      {analytics.dailyStats.map((day, idx) => (
                        <div
                          key={day.date}
                          className="flex-1 flex flex-col items-center group"
                          title={`${formatShortDate(day.date)}: ${day.analysisCount} análisis`}
                        >
                          <div
                            className="w-full bg-gradient-to-t from-cyan-500/60 to-purple-500/60 rounded-t transition-all group-hover:from-cyan-500 group-hover:to-purple-500"
                            style={{
                              height: `${Math.max((day.analysisCount / maxDailyCount) * 100, 2)}%`,
                              minHeight: day.analysisCount > 0 ? '4px' : '2px'
                            }}
                          />
                          {idx % 5 === 0 && (
                            <span className="text-[10px] text-zinc-600 mt-2 -rotate-45 origin-left whitespace-nowrap">
                              {formatShortDate(day.date)}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Top Users Table */}
                  <div className="bg-black/40 border border-zinc-800 rounded-xl overflow-hidden">
                    <div className="flex items-center gap-2 p-6 border-b border-zinc-800">
                      <Users className="w-5 h-5 text-purple-500" />
                      <h3 className="text-lg font-bold text-white">Top Usuarios</h3>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="text-left text-xs text-zinc-500 uppercase tracking-wider bg-zinc-900/50">
                            <th className="px-6 py-3 font-medium">#</th>
                            <th className="px-6 py-3 font-medium">Usuario</th>
                            <th className="px-6 py-3 font-medium">Email</th>
                            <th className="px-6 py-3 font-medium text-right">Análisis</th>
                            <th className="px-6 py-3 font-medium text-right">Última Actividad</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-zinc-800/50">
                          {analytics.topUsers.length === 0 ? (
                            <tr>
                              <td colSpan={5} className="px-6 py-8 text-center text-zinc-500">
                                No hay datos de usuarios todavía
                              </td>
                            </tr>
                          ) : (
                            analytics.topUsers.map((user, index) => (
                              <tr key={user.userId} className="hover:bg-zinc-800/30 transition-colors">
                                <td className="px-6 py-4">
                                  <span className={`text-sm font-bold ${index === 0 ? 'text-amber-400' :
                                    index === 1 ? 'text-zinc-400' :
                                      index === 2 ? 'text-amber-700' : 'text-zinc-600'
                                    }`}>
                                    {index + 1}
                                  </span>
                                </td>
                                <td className="px-6 py-4">
                                  <span className="text-sm text-white font-medium">{user.name}</span>
                                </td>
                                <td className="px-6 py-4">
                                  <span className="text-sm text-zinc-400">{user.email}</span>
                                </td>
                                <td className="px-6 py-4 text-right">
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-purple-500/10 text-purple-400 border border-purple-500/20">
                                    {user.analysisCount}
                                  </span>
                                </td>
                                <td className="px-6 py-4 text-right">
                                  <div className="flex items-center justify-end gap-1 text-xs text-zinc-500">
                                    <Clock className="w-3 h-3" />
                                    {formatDate(user.lastActive)}
                                  </div>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              ) : null}
            </>
          ) : (
            /* Modal Configuration Tab */
            modalConfig ? (
              <div className="space-y-6 max-w-3xl mx-auto">
                {/* Enable/Disable Toggle */}
                <div className="bg-black/40 border border-zinc-800 rounded-xl p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-bold text-white mb-1">Activar Modal</h3>
                      <p className="text-sm text-zinc-500">Mostrar modal publicitario a los usuarios</p>
                    </div>
                    <button
                      onClick={() => setModalConfig({ ...modalConfig, enabled: !modalConfig.enabled })}
                      className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors ${modalConfig.enabled ? 'bg-cyan-500' : 'bg-zinc-700'
                        }`}
                    >
                      <span
                        className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${modalConfig.enabled ? 'translate-x-7' : 'translate-x-1'
                          }`}
                      />
                    </button>
                  </div>
                </div>

                {/* Title */}
                <div className="bg-black/40 border border-zinc-800 rounded-xl p-6">
                  <label className="block text-sm font-bold text-white mb-2">Título</label>
                  <input
                    type="text"
                    value={modalConfig.title}
                    onChange={(e) => setModalConfig({ ...modalConfig, title: e.target.value })}
                    className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-cyan-500 transition-colors"
                    placeholder="Título del modal"
                  />
                </div>

                {/* Message */}
                <div className="bg-black/40 border border-zinc-800 rounded-xl p-6">
                  <label className="block text-sm font-bold text-white mb-2">Mensaje</label>
                  <textarea
                    value={modalConfig.message}
                    onChange={(e) => setModalConfig({ ...modalConfig, message: e.target.value })}
                    className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-cyan-500 transition-colors resize-none h-32"
                    placeholder="Mensaje publicitario"
                  />
                </div>

                {/* Link Text */}
                <div className="bg-black/40 border border-zinc-800 rounded-xl p-6">
                  <label className="block text-sm font-bold text-white mb-2">Texto del Enlace (Opcional)</label>
                  <input
                    type="text"
                    value={modalConfig.linkText || ''}
                    onChange={(e) => setModalConfig({ ...modalConfig, linkText: e.target.value })}
                    className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-cyan-500 transition-colors"
                    placeholder="Ej: Conocer más"
                  />
                </div>

                {/* Link URL */}
                <div className="bg-black/40 border border-zinc-800 rounded-xl p-6">
                  <label className="block text-sm font-bold text-white mb-2">URL del Enlace (Opcional)</label>
                  <input
                    type="url"
                    value={modalConfig.linkUrl || ''}
                    onChange={(e) => setModalConfig({ ...modalConfig, linkUrl: e.target.value })}
                    className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-cyan-500 transition-colors"
                    placeholder="https://ejemplo.com"
                  />
                </div>

                {/* Frequency */}
                <div className="bg-black/40 border border-zinc-800 rounded-xl p-6">
                  <label className="block text-sm font-bold text-white mb-2">Frecuencia de Aparición</label>
                  <select
                    value={modalConfig.frequency}
                    onChange={(e) => setModalConfig({ ...modalConfig, frequency: e.target.value as AdvertisingModalConfig['frequency'] })}
                    className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-cyan-500 transition-colors"
                  >
                    <option value="always">Siempre (cada análisis)</option>
                    <option value="once_per_session">Una vez por sesión</option>
                    <option value="once_per_day">Una vez por día</option>
                    <option value="once_per_week">Una vez por semana</option>
                    <option value="every_2_analyses">Cada 2 análisis</option>
                    <option value="every_3_analyses">Cada 3 análisis</option>
                    <option value="every_5_analyses">Cada 5 análisis</option>
                  </select>
                </div>

                {/* Timeout */}
                <div className="bg-black/40 border border-zinc-800 rounded-xl p-6">
                  <label className="block text-sm font-bold text-white mb-2">Tiempo de Espera (segundos)</label>
                  <p className="text-xs text-zinc-500 mb-3">Tiempo que el usuario debe esperar antes de poder cerrar el modal</p>
                  <input
                    type="number"
                    min="0"
                    max="60"
                    value={modalConfig.timeoutSeconds}
                    onChange={(e) => {
                      const value = parseInt(e.target.value, 10);
                      const timeoutSeconds = isNaN(value) ? 0 : Math.max(0, Math.min(60, value));
                      setModalConfig({ ...modalConfig, timeoutSeconds });
                    }}
                    className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-cyan-500 transition-colors"
                    placeholder="5"
                  />
                </div>

                {/* Button Text */}
                <div className="bg-black/40 border border-zinc-800 rounded-xl p-6">
                  <label className="block text-sm font-bold text-white mb-2">Texto del Botón</label>
                  <input
                    type="text"
                    value={modalConfig.buttonText}
                    onChange={(e) => setModalConfig({ ...modalConfig, buttonText: e.target.value })}
                    className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-cyan-500 transition-colors"
                    placeholder="Ej: Continuar al resultado"
                  />
                </div>

                {/* Save and Preview Buttons */}
                <div className="flex items-center justify-between gap-3 pt-4">
                  <button
                    onClick={() => setShowPreview(true)}
                    className="bg-zinc-800 hover:bg-zinc-700 text-white font-bold py-3 px-8 rounded-xl transition-all duration-200 flex items-center gap-2 border border-zinc-700 hover:border-zinc-600 active:scale-95"
                  >
                    <Eye className="w-4 h-4" />
                    Previsualizar
                  </button>
                  <div className="flex items-center gap-3">
                    {modalSaveSuccess && (
                      <span className="text-emerald-400 text-sm font-medium">¡Guardado exitosamente!</span>
                    )}
                    <button
                      onClick={handleSaveModalConfig}
                      disabled={isSavingModal}
                      className="bg-gradient-to-r from-cyan-500 to-purple-500 hover:from-cyan-400 hover:to-purple-400 text-white font-bold py-3 px-8 rounded-xl transition-all duration-200 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed active:scale-95"
                    >
                      {isSavingModal ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          Guardando...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4" />
                          Guardar Configuración
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-64">
                <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
              </div>
            )
          )}
        </div>
      </div>

      {/* Preview Modal */}
      {modalConfig && (
        <AdvertisingModal
          isOpen={showPreview}
          config={{ ...modalConfig, enabled: true }}
          onClose={() => setShowPreview(false)}
        />
      )}
    </>
  );
};

interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: number;
  color: 'cyan' | 'purple' | 'emerald' | 'amber';
}

const StatCard: React.FC<StatCardProps> = ({ icon, label, value, color }) => {
  const colorClasses = {
    cyan: 'text-cyan-400 bg-cyan-500/10 border-cyan-500/20',
    purple: 'text-purple-400 bg-purple-500/10 border-purple-500/20',
    emerald: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20',
    amber: 'text-amber-400 bg-amber-500/10 border-amber-500/20'
  };

  return (
    <div className="bg-black/40 border border-zinc-800 rounded-xl p-5 hover:border-zinc-700 transition-colors">
      <div className={`inline-flex p-2 rounded-lg border mb-3 ${colorClasses[color]}`}>
        {icon}
      </div>
      <div className="text-3xl font-black text-white mb-1">{value.toLocaleString('es-AR')}</div>
      <div className="text-xs text-zinc-500 font-medium uppercase tracking-wider">{label}</div>
    </div>
  );
};

export default AdminDashboard;
